/* Timing for the intro swirl, kept pure so it can be reasoned about and tested
 * without a WebGL context.
 *
 * The swirl is the dragon's entrance: every particle starts scattered into a
 * rotating cloud and walks back to its settled surface position. The shape of
 * that walk is one number - `uSwirl` - handed to the vertex shader, so the
 * per-particle work stays on the GPU and the CPU only advances a clock.
 *
 * The one thing this module must get right is *when* the clock starts. It does
 * not start on load. The site mounts underneath an opaque intro overlay so the
 * WebGL context and the 450,000-point buffers are warm before the curtain
 * lifts (see `revealContext.js`), which means anything keyed to load completion
 * plays to an empty room on a fast connection. The caller therefore starts this
 * clock on the LATER of the reveal flip and geometry arrival; see
 * `swirlStarted` below, which is that rule written down.
 */

/** Whether the swirl clock may run: the curtain is up and there is a dragon. */
export function swirlStarted(revealed, hasGeometry) {
  return Boolean(revealed) && Boolean(hasGeometry);
}

/**
 * How dispersed the cloud is at one instant.
 *
 * @param {number} elapsedMs time since the swirl started
 * @param {number} durationMs total swirl length; 0 or less disables the swirl
 * @param {number} ease exponent on the remaining distance. 1 is linear, higher
 *   pulls most of the travel into the opening moments and lets the figure
 *   settle slowly, which is what reads as particles finding their place rather
 *   than sliding to a stop.
 * @returns {number} 1 when fully scattered, 0 when settled on the surface
 */
export function swirlProgress(elapsedMs, durationMs, ease) {
  if (!(durationMs > 0)) return 0;
  if (!(elapsedMs > 0)) return 1;
  const p = Math.min(1, elapsedMs / durationMs);
  const exponent = ease > 0 ? ease : 1;
  return Math.pow(1 - p, exponent);
}

/**
 * How far the particles have faded up from the page colour.
 *
 * Separate from the swirl itself and much shorter: the cloud should be visible
 * for nearly the whole convergence, and this only takes the hard edge off its
 * first appearance. Fading is done by mixing colour and shrinking the sprite,
 * never by material opacity - the material's `alphaTest` discards a fading
 * sprite wholesale partway down rather than easing it out.
 *
 * @param {number} elapsedMs time since the swirl started
 * @param {number} fadeMs fade length; 0 or less means no fade at all
 * @returns {number} 0 at the page colour, 1 at the particle's own colour
 */
export function swirlFade(elapsedMs, fadeMs) {
  if (!(fadeMs > 0)) return 1;
  if (!(elapsedMs > 0)) return 0;
  return Math.min(1, elapsedMs / fadeMs);
}
